import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { debounce } from "lodash";
import toast from "react-hot-toast";
import { getActiveFilters } from "./utils";
import useUserLogs from "./hooks/useUserLogs";
import { getUserLogs, getUserServiceUsers } from "../../services/apis/monitorApis";
import UserLogsTable from "./components/UserLogsTable";
import UserLogsFilterSection from "./components/UserLogsFilterSection";
import AppLeftSideBar from "../../components/SideBar/AppLeftSideBar";
import Input from "../../components/FormComponents/Input";
import Button from "../../components/FormComponents/Button";

const INITIAL_FILTERS = {
  type: undefined,
  user_email: undefined,
  from_date: undefined,
  to_date: undefined
};

const UserLogs = () => {
  const limit = 100;
  const [pagination, setPagination] = useState({
    search: undefined,
    page: 1,
    limit: Number(limit)
  });
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [searchQuery, setSearchQuery] = useState("");
  const [userOptions, setUserOptions] = useState([]);

  const { logs, pageTrack, fetchLogs, setLogs, setPageTrack, logTypeOptions } = useUserLogs();

  const debouncedSearchRef = useRef(null);
  const debouncedFilterRef = useRef(null);

  const refreshLogs = useCallback(async () => {
    const activeFilters = getActiveFilters(filters);
    await fetchLogs(pagination.search, pagination.page, pagination.limit, activeFilters);
  }, [filters, pagination, fetchLogs]);

  const handleSearchInputChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    setPagination((prev) => ({ ...prev, search: query, page: 1 }));
    debouncedSearchRef.current(query);
  };

  const handleTypeChange = (e) => {
    const value = e.target.value || undefined;
    setFilters((prev) => {
      const updatedFilters = { ...prev, type: value };
      debouncedFilterRef.current(updatedFilters);
      return updatedFilters;
    });
  };

  const handleUserSelect = (option) => {
    const userEmail = option?.email || option?.user_email || undefined;
    setFilters((prev) => {
      const updatedFilters = { ...prev, user_email: userEmail };
      debouncedFilterRef.current(updatedFilters);
      return updatedFilters;
    });
  };

  const toLocalDatetimeString = (isoString) => {
    if (!isoString) return "";
    const date = new Date(isoString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const toUTCISOString = (localDatetime) => {
    if (!localDatetime) return undefined;
    const date = new Date(localDatetime);
    return date.toISOString();
  };

  const handleDateChange = (key, value) => {
    setFilters((prev) => {
      const updatedFilters = { ...prev, [key]: value };
      debouncedFilterRef.current(updatedFilters);
      return updatedFilters;
    });
  };

  const fetchUserOptions = useMemo(
    () =>
      debounce(async (term) => {
        try {
          const response = await getUserServiceUsers({
            search: term?.trim() || undefined,
            page: 1,
            limit: 20
          });

          const data = response?.data || response;
          const users = data?.data || data || [];
          const normalizedUsers = Array.isArray(users)
            ? users.map((user) => ({
                ...user,
                email: user?.email || user?.user_email || ""
              }))
            : [];
          setUserOptions(normalizedUsers);
        } catch (error) {
          console.error("Failed to fetch users:", error);
          setUserOptions([]);
        }
      }, 300),
    []
  );

  const handleRefresh = async () => {
    try {
      debouncedSearchRef.current?.cancel();
      debouncedFilterRef.current?.cancel();
      await refreshLogs();
      toast.success("User logs refreshed successfully");
    } catch (error) {
      console.error("Refresh error:", error);
    }
  };

  const onPageChangeAPI = async (pageNumber) => {
    try {
      const activeFilters = getActiveFilters(filters);
      await fetchLogs(pagination.search, pageNumber, pagination.limit, activeFilters);
      setPagination((prev) => ({ ...prev, page: pageNumber }));
    } catch (error) {
      console.error("Failed to fetch user logs:", error);
    }
  };

  const onLimitChangeAPI = async (newLimit) => {
    try {
      localStorage.setItem("limit", newLimit);
      const activeFilters = getActiveFilters(filters);
      await fetchLogs(pagination.search, pagination.page, newLimit, activeFilters);
      setPagination((prev) => ({ ...prev, limit: newLimit }));
    } catch (error) {
      console.error("Failed to fetch user logs:", error);
    }
  };

  useEffect(() => {
    debouncedSearchRef.current = debounce(async (query) => {
      try {
        const activeFilters = getActiveFilters(filters);

        const response = await getUserLogs({
          search: query?.trim() || undefined,
          page: 1,
          limit: pagination.limit,
          ...activeFilters
        });

        const data = response?.data;
        setLogs(data?.logs || []);
        setPageTrack({
          currPage: data?.page,
          totalPages: data?.total_pages
        });
        setPagination((prev) => ({ ...prev, page: 1 }));
      } catch (error) {
        console.error("Search error:", error);
      }
    }, 500);

    return () => debouncedSearchRef.current?.cancel();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, pagination.limit]);

  useEffect(() => {
    debouncedFilterRef.current = debounce(async (filtersToApply) => {
      try {
        const activeFilters = getActiveFilters(filtersToApply);
        const response = await getUserLogs({
          search: pagination.search || undefined,
          page: 1,
          limit: pagination.limit,
          ...activeFilters
        });

        const data = response?.data;
        setLogs(data?.logs || []);
        setPageTrack({
          currPage: data?.page,
          totalPages: data?.total_pages
        });
        setPagination((prev) => ({ ...prev, page: 1 }));
      } catch (error) {
        console.error("Filter error:", error);
      }
    }, 500);

    return () => debouncedFilterRef.current?.cancel();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.search, pagination.limit]);

  useEffect(() => {
    fetchLogs(undefined, 1, Number(limit));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return () => {
      fetchUserOptions.cancel();
    };
  }, [fetchUserOptions]);

  return (
    <div className="application_container">
      <AppLeftSideBar type="org" />
      <div className="h_100 d_flex flex_column gap_2 w_100 p_3">
        <div className="w_100 h_100 d_flex justify_between">
          <h2>User Logs</h2>
          <div className="d_flex gap_2">
            <Input
              autoFocus
              placeholder="Search user logs..."
              value={searchQuery}
              onChange={handleSearchInputChange}
              className="w_100"
            />
            <div className="w_25">
              <Button variant="primary" onClick={handleRefresh} title="Refresh user logs">
                Refresh
              </Button>
            </div>
          </div>
        </div>

        <UserLogsFilterSection
          filters={filters}
          logTypeOptions={logTypeOptions}
          userOptions={userOptions}
          onTypeChange={handleTypeChange}
          onUserSelect={handleUserSelect}
          onUserSearch={fetchUserOptions}
          onDateChange={handleDateChange}
          toLocalDatetimeString={toLocalDatetimeString}
          toUTCISOString={toUTCISOString}
        />

        <UserLogsTable
          logs={logs}
          pageTrack={pageTrack}
          pagination={pagination}
          onPageChange={onPageChangeAPI}
          onLimitChange={onLimitChangeAPI}
        />
      </div>
    </div>
  );
};

export default UserLogs;
